import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './Navbar.css';
import {
  IconLending, IconInsurance, IconEcommerce, IconLogistics, IconGaming, IconEnterprise,
  IconAbout, IconBlog, IconFAQ, IconContact,
} from './NavIcons';

const navIndustries = [
  { icon: IconLending,    title: 'Lending & NBFCs',          subtitle: 'Instant disbursals, EMI reconciliation',    href: '/industries/lending' },
  { icon: IconInsurance,  title: 'Insurance',                 subtitle: 'Faster claims and premium refunds',         href: '/industries/insurance' },
  { icon: IconEcommerce,  title: 'E-commerce & Marketplaces', subtitle: 'Scheduled seller and vendor payouts',       href: '/industries/ecommerce' },
  { icon: IconLogistics,  title: 'Logistics & Delivery',      subtitle: 'Daily rider and gig partner payouts',       href: '/industries/logistics' },
  { icon: IconGaming,     title: 'Media',                     subtitle: 'Fast, KYC-checked creator payouts',         href: '/industries/media' },
  { icon: IconEnterprise, title: 'Enterprise & Payroll',      subtitle: 'Scheduled payroll and vendor runs',         href: '/industries/enterprise' },
];

const navCompany = [
  { icon: IconAbout,   title: 'About',   subtitle: 'Who we are and how we work',            href: '/about' },
  { icon: IconBlog,    title: 'Blog',    subtitle: 'Notes on payouts, ledgers and compliance', href: '/blog' },
  { icon: IconFAQ,     title: 'FAQ',     subtitle: 'Quick answers to common questions',      href: '/faq' },
  { icon: IconContact, title: 'Contact', subtitle: 'Reach sales, support or partnerships',   href: '/contact' },
];

const companyPaths = ['/about', '/contact', '/blog', '/faq'];

export default function Navbar() {
  const location = useLocation();
  const isCompanyActive = companyPaths.some(p => location.pathname.startsWith(p));
  const [open, setOpen] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const closeTimer = useRef(null);
  const navRef = useRef(null);

  const show = (name) => { clearTimeout(closeTimer.current); setOpen(name); };
  const hide = () => { closeTimer.current = setTimeout(() => setOpen(null), 120); };

  const toggleMobileAccordion = (name) => {
    setMobileExpanded(prev => prev === name ? null : name);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(null); setMobileOpen(false); }
    };
    const onOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpen(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onOutside);
    };
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [mobileOpen]);

  const chevron = (
    <span className="navbar__chevron">
      <svg xmlns="http://www.w3.org/2000/svg" width="9" height="6" viewBox="0 0 9 6" fill="none">
        <path d="M0.75 0.75L4.5 4.5L8.25 0.75" stroke="black" strokeOpacity="0.42" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </span>
  );

  return (
    <nav className="navbar" ref={navRef}>
      <a href="/" className="navbar__logo" aria-label="settlor.money">
        <svg width="106" height="36" viewBox="0 0 1474 506" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M310.643 126.379V5.26368L267.153 1.12299C194.669 -5.86441 122.963 20.0149 71.4478 71.5147C25.628 117.58 0 179.949 0 244.388C0 252.152 0 311.157 0 311.157H126.069V432.272L169.559 436.413C242.043 443.4 313.749 417.521 365.264 366.021C411.343 319.956 436.712 257.845 436.712 193.406C436.712 185.642 436.712 126.638 436.712 126.638H310.643V126.379ZM310.643 311.157C287.345 334.448 257.316 347.129 226.769 349.199C190.786 351.529 154.027 338.33 126.587 310.898L218.485 218.768L88.2743 210.486C90.0864 179.949 102.771 149.67 126.328 126.379C149.626 103.087 179.655 90.4065 210.202 88.3362C246.184 86.007 282.685 99.4643 310.125 126.638L218.485 218.768L348.697 227.049C346.626 257.587 333.941 287.607 310.643 311.157Z" fill="#0043FF"/>
          <path d="M675.13 131.555C665.811 127.931 653.903 124.826 640.701 121.462C635.523 120.168 630.346 118.874 625.169 117.321C607.048 112.145 597.987 108.004 597.987 98.1703C597.987 88.3362 607.565 84.1955 624.133 84.1955C644.066 84.1955 653.126 89.889 655.456 106.193L687.038 73.3262C675.907 58.575 655.974 51.07 625.427 51.07C574.43 51.07 555.015 72.5498 555.015 100.758C555.015 115.768 560.451 128.708 575.983 138.542C584.526 143.977 594.881 147.859 610.672 152.258C616.885 153.811 623.615 155.622 631.64 157.693C645.878 161.057 658.563 164.939 658.563 176.843C658.563 189.524 644.584 194.182 629.052 194.182C607.565 194.182 594.363 184.866 594.363 169.338L561.487 203.24C574.689 218.25 597.728 227.567 629.31 227.567C676.942 227.567 702.312 206.863 702.312 172.185C702.829 150.188 692.992 138.542 675.13 131.555Z" fill="#0043FF"/>
          <path d="M790.068 192.371C768.323 192.371 754.085 176.067 752.791 150.705H867.988C867.988 84.1954 840.806 50.8111 789.55 50.8111C738.294 50.8111 708.524 84.1954 708.524 135.954C708.524 196.512 736.482 227.308 792.139 227.308C827.863 227.308 855.303 207.898 865.658 173.479H820.097C816.991 185.125 804.306 192.371 790.068 192.371ZM787.997 86.2658C805.341 86.2658 821.65 98.1702 822.427 123.014H754.085C755.639 99.723 770.394 86.2658 787.997 86.2658Z" fill="#0043FF"/>
          <path d="M1077.67 4.22846V227.308H1121.94V25.4495L1077.67 4.22846Z" fill="#0043FF"/>
          <path d="M1222.12 51.07C1169.05 51.07 1136.18 86.2658 1136.18 139.318C1136.18 191.336 1168.02 227.567 1222.12 227.567C1274.15 227.567 1307.03 192.371 1307.03 139.318C1307.03 85.7483 1274.41 51.07 1222.12 51.07ZM1222.12 190.301C1195.2 190.301 1181.74 173.22 1181.74 139.318C1181.74 105.158 1195.46 88.595 1222.12 88.595C1249.04 88.595 1262.5 105.158 1262.5 139.318C1262.5 173.22 1248.78 190.301 1222.12 190.301Z" fill="#0043FF"/>
          <path d="M1419.64 177.102H1418.34C1405.66 177.102 1395.3 187.454 1395.3 200.135C1395.3 212.815 1405.66 223.167 1418.34 223.167H1419.64C1426.11 223.167 1431.8 220.579 1435.95 216.439C1440.09 212.298 1442.68 206.604 1442.68 200.135C1442.68 187.454 1432.32 177.102 1419.64 177.102Z" fill="#0043FF"/>
          <path d="M895.946 260.175C842.877 260.175 810.001 295.371 810.001 348.423C810.001 400.44 841.842 436.671 895.946 436.671C947.978 436.671 980.855 401.476 980.855 348.423C980.855 294.853 948.237 260.175 895.946 260.175ZM895.946 399.405C869.023 399.405 855.562 382.325 855.562 348.423C855.562 314.262 869.023 297.7 895.946 297.7C922.868 297.7 936.329 314.262 936.329 348.423C936.329 382.325 922.609 399.405 895.946 399.405Z" fill="#0043FF"/>
          <path d="M1233.77 401.476C1212.02 401.476 1197.79 385.172 1196.49 359.81H1311.69C1311.69 293.3 1284.51 259.916 1233.25 259.916C1182 259.916 1152.23 293.3 1152.23 345.059C1152.23 405.616 1180.18 436.413 1235.84 436.413C1271.56 436.413 1299 417.003 1309.36 382.584H1263.8C1260.43 394.229 1248.01 401.476 1233.77 401.476ZM1231.7 295.112C1249.04 295.112 1265.35 307.016 1266.13 331.86H1197.79C1199.34 308.828 1213.84 295.112 1231.7 295.112Z" fill="#0043FF"/>
          <path d="M1474 264.574V505.51H1429.73V410.016C1423.52 425.026 1405.14 433.825 1384.43 433.825C1349.48 433.825 1323.34 410.016 1323.34 373.267V264.574H1367.6V366.797C1367.6 387.501 1377.18 398.111 1395.82 398.111C1416.01 398.111 1429.73 384.913 1429.73 366.797V264.574H1474Z" fill="#0043FF"/>
          <path d="M1416.53 96.3588H1385.47C1373.56 96.3588 1363.98 105.934 1363.98 117.839V223.167H1319.46V103.087C1319.46 74.3614 1342.75 51.07 1371.49 51.07H1416.53V96.3588Z" fill="#0043FF"/>
          <path d="M1137.21 315.815V432.013H1092.69V330.566C1092.69 304.687 1085.18 297.441 1064.21 297.441H1037.55V432.013H993.28V260.175H1081.81C1113.91 260.175 1137.21 279.584 1137.21 315.815Z" fill="#0043FF"/>
          <path d="M791.88 307.016V423.214H746.06V310.121C746.06 298.217 736.482 288.642 724.574 288.642H697.393V423.214H653.126V310.121C653.126 298.217 643.548 288.642 631.64 288.642H604.459V423.214H560.192V251.375H737.259C771.171 251.375 791.88 275.702 791.88 307.016Z" fill="#0043FF"/>
          <path d="M1022.27 86.7834V155.622C1022.27 176.584 1029.78 189.007 1047.64 189.007V227.567H1047.13C998.199 227.567 978.784 196.253 978.784 160.022V86.7834V55.4694V4.22846L990.951 10.1807L1022.27 25.1907V55.4694H1057.22V86.7834H1022.27Z" fill="#0043FF"/>
          <path d="M923.386 86.7835V155.622C923.386 176.585 930.893 189.007 948.755 189.007V227.567H947.978C899.052 227.567 879.637 196.253 879.637 160.022V4.48734L891.286 10.1808L923.127 25.4496V55.4695H978.525V86.7835H923.386Z" fill="#0043FF"/>
        </svg>
      </a>
      <div className="navbar__links">
        <NavLink to="/" end className={({ isActive }) => `navbar__link${isActive ? ' navbar__link--active' : ''}`}>Home</NavLink>
        <NavLink to="/payout" className={({ isActive }) => `navbar__link${isActive ? ' navbar__link--active' : ''}`}>Payout</NavLink>

        <div
          className="navbar__link-wrapper"
          onMouseEnter={() => show('industries')}
          onMouseLeave={hide}
        >
          <NavLink
            to="/industries"
            className={({ isActive }) => `navbar__link navbar__link--dropdown${isActive ? ' navbar__link--active' : ''}`}
          >
            Industries{chevron}
          </NavLink>
          {open === 'industries' && (
            <div className="navbar__dropdown" onMouseEnter={() => show('industries')} onMouseLeave={hide}>
              <div className="navbar__dropdown-inner">
                {navIndustries.map((item, i) => (
                  <Link key={i} to={item.href} className="navbar__dropdown-item" onClick={() => setOpen(null)}>
                    <span className="navbar__dropdown-icon">{item.icon}</span>
                    <span className="navbar__dropdown-text">
                      <span className="navbar__dropdown-title">{item.title}</span>
                      <span className="navbar__dropdown-subtitle">{item.subtitle}</span>
                    </span>
                  </Link>
                ))}
                <Link to="/industries" className="navbar__dropdown-footer" onClick={() => setOpen(null)}>
                  All industries
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path d="M2.70825 6.5H10.2916" stroke="#0043FE" strokeWidth="1.35417" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6.5 2.70801L10.2917 6.49967L6.5 10.2913" stroke="#0043FE" strokeWidth="1.35417" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </div>
            </div>
          )}
        </div>

        <NavLink to="/partner" className={({ isActive }) => `navbar__link${isActive ? ' navbar__link--active' : ''}`}>
          Partner with us
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M5.99999 0.900391C6.29999 4.00039 7.99999 5.70039 11.1 6.00039C7.99999 6.30039 6.29999 8.00039 5.99999 11.1004C5.69999 8.00039 3.99999 6.30039 0.899994 6.00039C3.99999 5.70039 5.69999 4.00039 5.99999 0.900391Z" fill="#0095FC"/></svg>
        </NavLink>

        <div
          className="navbar__link-wrapper"
          onMouseEnter={() => show('company')}
          onMouseLeave={hide}
        >
          <NavLink
            to="/about"
            className={`navbar__link navbar__link--dropdown${isCompanyActive ? ' navbar__link--active' : ''}`}
          >
            Company{chevron}
          </NavLink>
          {open === 'company' && (
            <div className="navbar__dropdown navbar__dropdown--company" onMouseEnter={() => show('company')} onMouseLeave={hide}>
              <div className="navbar__dropdown-inner">
                {navCompany.map((item, i) => (
                  <Link key={i} to={item.href} className="navbar__dropdown-item" onClick={() => setOpen(null)}>
                    <span className="navbar__dropdown-icon">{item.icon}</span>
                    <span className="navbar__dropdown-text">
                      <span className="navbar__dropdown-title">{item.title}</span>
                      <span className="navbar__dropdown-subtitle">{item.subtitle}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <a href="#contact" className="navbar__cta navbar__cta--desktop">
        Login / Signup
        <span className="navbar__cta-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
            <path d="M3.125 7.5H11.875" stroke="white" strokeWidth="1.5625" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M7.5 3.125L11.875 7.5L7.5 11.875" stroke="white" strokeWidth="1.5625" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </a>

      <button
        className={`navbar__hamburger${mobileOpen ? ' navbar__hamburger--open' : ''}`}
        onClick={() => setMobileOpen(v => !v)}
        aria-label="Toggle menu"
        aria-expanded={mobileOpen}
      >
        <span /><span /><span />
      </button>

      {mobileOpen && (
        <div className="navbar__mobile-drawer">
          <NavLink to="/" end className={({ isActive }) => `navbar__mobile-link${isActive ? ' navbar__mobile-link--active' : ''}`} onClick={() => setMobileOpen(false)}>Home</NavLink>
          <NavLink to="/payout" className={({ isActive }) => `navbar__mobile-link${isActive ? ' navbar__mobile-link--active' : ''}`} onClick={() => setMobileOpen(false)}>Payout</NavLink>

          <button
            className="navbar__mobile-link navbar__mobile-accordion"
            onClick={() => toggleMobileAccordion('industries')}
            aria-expanded={mobileExpanded === 'industries'}
          >
            Industries
            <span className={`navbar__mobile-chevron${mobileExpanded === 'industries' ? ' navbar__mobile-chevron--open' : ''}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="9" height="6" viewBox="0 0 9 6" fill="none">
                <path d="M0.75 0.75L4.5 4.5L8.25 0.75" stroke="black" strokeOpacity="0.42" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
          {mobileExpanded === 'industries' && (
            <div className="navbar__mobile-submenu">
              {navIndustries.map((item, i) => (
                <Link key={i} to={item.href} className="navbar__mobile-subitem" onClick={() => setMobileOpen(false)}>
                  <span className="navbar__dropdown-icon">{item.icon}</span>
                  <span className="navbar__dropdown-text">
                    <span className="navbar__dropdown-title">{item.title}</span>
                    <span className="navbar__dropdown-subtitle">{item.subtitle}</span>
                  </span>
                </Link>
              ))}
            </div>
          )}
          <NavLink to="/partner" className={({ isActive }) => `navbar__mobile-link${isActive ? ' navbar__mobile-link--active' : ''}`} onClick={() => setMobileOpen(false)}>Partner with us</NavLink>

          <button
            className="navbar__mobile-link navbar__mobile-accordion"
            onClick={() => toggleMobileAccordion('company')}
            aria-expanded={mobileExpanded === 'company'}
          >
            Company
            <span className={`navbar__mobile-chevron${mobileExpanded === 'company' ? ' navbar__mobile-chevron--open' : ''}`}>
              <svg xmlns="http://www.w3.org/2000/svg" width="9" height="6" viewBox="0 0 9 6" fill="none">
                <path d="M0.75 0.75L4.5 4.5L8.25 0.75" stroke="black" strokeOpacity="0.42" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
          {mobileExpanded === 'company' && (
            <div className="navbar__mobile-submenu">
              {navCompany.map((item, i) => (
                <Link key={i} to={item.href} className="navbar__mobile-subitem" onClick={() => setMobileOpen(false)}>
                  <span className="navbar__dropdown-icon">{item.icon}</span>
                  <span className="navbar__dropdown-text">
                    <span className="navbar__dropdown-title">{item.title}</span>
                    <span className="navbar__dropdown-subtitle">{item.subtitle}</span>
                  </span>
                </Link>
              ))}
            </div>
          )}

          <a href="#contact" className="navbar__cta navbar__cta--mobile" onClick={() => setMobileOpen(false)}>
            Login / Signup
            <span className="navbar__cta-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path d="M3.125 7.5H11.875" stroke="white" strokeWidth="1.5625" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7.5 3.125L11.875 7.5L7.5 11.875" stroke="white" strokeWidth="1.5625" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </a>
        </div>
      )}
    </nav>
  );
}
